import { defineStyle, defineStyleConfig, SystemStyleObject } from '@chakra-ui/styled-system';
import { rem } from '../../utils/utils';
import { CodeSnippetProps } from './CodeSnippet';

const contentPadding = (variant: CodeSnippetProps['variant']) => {
  switch (variant) {
    case 'single':
      return {
        padding: '12',
        paddingLeft: '16',
        paddingRight: '0',
      };
    case 'multi':
    case 'multi-breakwords':
      return {
        padding: '16',
      };
    default:
      return {
        paddingX: rem(6),
        paddingY: rem(2),
      };
  }
};

const baseStyle = defineStyle(({ variant, size, isExpanded }): SystemStyleObject => {
  let copyContainerWrapping = 'pre';
  if (variant === 'single') {
    copyContainerWrapping = 'nowrap';
  }
  if (variant === 'multi-breakwords') {
    copyContainerWrapping = 'break-spaces';
  }

  const isMultiline = variant === 'multi' || variant === 'multi-breakwords';

  return {
    wrapper: {
      borderRadius: '2',
      display: 'flex',
      position: 'relative',
      width: variant !== 'inline' ? '100%' : 'fit-content',
    },
    copyContainer: {
      _after:
        variant === 'single'
          ? {
              content: `""`,
              position: 'absolute',
              width: '8',
              top: '0',
              bottom: '0',
              right: '0',
              bgGradient: `linear(to-r, transparent, background/secondary)`,
            }
          : 'none',
      _focusVisible: {
        boxShadow: 'outline',
        zIndex: 1,
      },
      bg: 'background/secondary',
      borderRadius: '2',
      borderTopRightRadius: variant === 'single' ? '0' : '2',
      borderBottomRightRadius: variant === 'single' ? '0' : '2',
      cursor: 'pointer',
      div: {
        overflow: variant !== 'multi-breakwords' && 'auto !important',
      },
      minHeight: variant === 'inline' ? '24' : '40',
      overflow: 'auto',
      scrollbarHeight: variant === 'single' ? 'none' : undefined,
      '::-webkit-scrollbar': {
        height: variant === 'single' ? 0 : undefined,
      },
      position: variant === 'single' ? 'relative' : 'static',
      textColor: 'text/body',
      textStyle: size === 'md' ? 'code/md' : 'code/lg',
      lineHeight: rem(16),
      width: '100%',
      whiteSpace: copyContainerWrapping,
      ...contentPadding(variant),
    },
    copyIcon: {
      bg: 'background/secondary',
      border: 'none',
      borderRadius: isMultiline ? '2' : 'unset',
      borderTopRightRadius: '2',
      borderBottomRightRadius: '2',
      position: isMultiline ? 'absolute' : 'relative',
      px: isMultiline ? '4' : '12',
      py: isMultiline ? '4' : '8',
      right: isMultiline ? '4' : '0',
      svg: {
        color: 'icon/secondary',
      },
      top: isMultiline ? '4' : '0',
      _hover: {
        bg: 'background/hover',
        svg: {
          color: 'text/primary',
        },
      },
      _active: {
        bg: 'background/active',
        svg: {
          color: 'text/primary',
        },
      },
      _focusVisible: {
        boxShadow: 'outline',
        zIndex: 1,
      },
    },
    showMoreButtonContainer: {
      background: 'background/secondary',
      borderRadius: 4,
      bottom: 4,
      button: {
        color: 'text/secondary',
      },
      padding: 4,
      position: 'absolute',
      right: 4,
      svg: {
        transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
        transition: '300ms',
      },
      textAlign: 'right',
      _hover: {
        bg: 'background/hover',
        button: {
          color: 'text/primary',
        },
      },
      _active: {
        bg: 'background/active',
        button: {
          color: 'text/primary',
        },
      },
      _focusVisible: {
        boxShadow: 'outline',
      },
    },
  };
});

const CodeSnippetTheme = defineStyleConfig({
  baseStyle,
});

export default CodeSnippetTheme;
